import { useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import CratesPanel from '@/components/CratesPanel';
import PrepaymentsPanel from '@/components/PrepaymentsPanel';

/**
 * Counter jobs that are not a sale — crates going out and coming back, and
 * money taken for bread to be collected later.
 *
 * These live off the till rather than the manager dashboard because the person
 * doing them is the cashier standing at the counter, and cashiers have no
 * route into the dashboard.
 */
export default function CounterPage({ onBack }: { onBack: () => void }) {
  const { staff, activeBranchId, activeBranchName } = useAuthStore();
  const [tab, setTab] = useState<'crates' | 'prepayments'>('crates');
  const isManager = staff?.role === 'MANAGER';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <img src="/mepco-badge.png" alt="" className="w-9 h-9 shrink-0" />
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-amber-600 leading-tight">Counter</h1>
            <p className="text-xs text-gray-600 truncate">
              {staff?.name} · {activeBranchName}
            </p>
          </div>
        </div>
        <button
          onClick={onBack}
          className="bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold px-4 py-2 rounded shrink-0"
        >
          Back to till
        </button>
      </div>

      <div className="bg-white border-b px-4">
        <div className="max-w-6xl mx-auto flex gap-5">
          {(
            [
              ['crates', 'Crates'],
              ['prepayments', 'Prepayments'],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`py-3 border-b-2 font-medium text-sm ${
                tab === id
                  ? 'border-amber-500 text-amber-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {tab === 'crates' ? (
          <CratesPanel branchId={activeBranchId} branchName={activeBranchName} />
        ) : (
          <PrepaymentsPanel
            branchId={activeBranchId}
            branchName={activeBranchName}
            isManager={isManager}
          />
        )}
      </div>
    </div>
  );
}
